import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import WhatsAppButton from "@/components/WhatsAppButton";
import { PostRequirementDialog } from "@/components/PostRequirementDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import api from "@/lib/api";
import { Building2, Plus, Loader2, Users, CalendarDays, IndianRupee, Save, Upload, Briefcase, ArrowLeft, CheckCircle2, XCircle, Mail, FileText } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const mediaUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-600",
};

export default function EmployerDashboard() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const fileRef = useRef(null);

  const loadReqs = () => api.get("/employer/requirements").then(({ data }) => setReqs(data)).catch(() => {});
  const loadJobs = () => api.get("/employer/jobs").then(({ data }) => setJobs(data)).catch(() => {});

  useEffect(() => {
    api.get("/employer/profile").then(({ data }) => setProfile(data)).catch(() => {});
    loadReqs();
    loadJobs();
  }, []);

  const openJob = async (job) => {
    setActiveJob(job);
    setLoadingApps(true);
    setApplicants([]);
    try {
      const { data } = await api.get(`/employer/jobs/${job.job_id}/applications`);
      setApplicants(data);
    } catch (e) {
      toast.error(t("employerDash.applicantsError"));
    } finally {
      setLoadingApps(false);
    }
  };

  const setApplicantStatus = async (application_id, status) => {
    try {
      await api.patch(`/employer/applications/${application_id}`, { status });
      setApplicants((prev) => prev.map((a) => (a.application_id === application_id ? { ...a, status } : a)));
      toast.success(status === "Shortlisted" ? t("employerDash.shortlistedMsg") : t("employerDash.statusUpdated"));
    } catch (e) {
      toast.error(t("employerDash.statusError"));
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/employer/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile((p) => ({ ...p, logo_url: data.logo_url }));
      toast.success(t("employerDash.logoUploaded"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("employerDash.logoError"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setP = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/employer/profile", {
        company_name: profile.company_name || "",
        industry: profile.industry || "",
        website: profile.website || "",
        location: profile.location || "",
        company_size: profile.company_size || "",
        about: profile.about || "",
        logo_url: profile.logo_url || "",
      });
      setProfile(data);
      toast.success(t("employerDash.saved"));
    } catch (err) {
      toast.error(t("employerDash.saveError"));
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#FFFDF9]"><Navbar />
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-amber-600" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF9]" data-testid="employer-dashboard">
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-base font-semibold uppercase tracking-wider text-amber-600">{t("employerDash.eyebrow")}</p>
            <h1 className="mt-2 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
              {t("employerDash.title")}
            </h1>
          </div>
          <PostRequirementDialog
            mode="employer"
            onDone={loadReqs}
            trigger={
              <Button className="h-12 bg-amber-600 hover:bg-amber-700 px-6 text-base font-bold" data-testid="post-requirement-btn">
                <Plus className="mr-2 h-5 w-5" /> {t("employerDash.postRequirement")}
              </Button>
            }
          />
        </div>

        <div className="grid gap-8 lg:grid-cols-[1fr_1.3fr]">
          {/* Company profile */}
          <form onSubmit={saveProfile} className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm h-fit" data-testid="employer-profile-form">
            <div className="flex items-center gap-2 mb-5">
              <Building2 className="h-5 w-5 text-amber-600" />
              <h2 className="font-heading text-xl font-bold text-slate-900">{t("employerDash.companyProfile")}</h2>
            </div>
            <div className="space-y-4">
              <div>
                <Label>{t("employerDash.logo")}</Label>
                <div className="mt-1.5 flex items-center gap-4">
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center">
                    {profile.logo_url ? (
                      <img src={mediaUrl(profile.logo_url)} alt="logo" className="h-full w-full object-contain" data-testid="logo-preview" />
                    ) : (
                      <span className="text-2xl font-extrabold text-amber-600">{(profile.company_name || "C")[0]?.toUpperCase()}</span>
                    )}
                  </div>
                  <div>
                    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={uploadLogo} className="hidden" data-testid="logo-input" />
                    <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="upload-logo-btn">
                      {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} {t("employerDash.uploadLogo")}
                    </Button>
                    <p className="mt-1 text-xs text-slate-400">{t("employerDash.logoHint")}</p>
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="cp-name">{t("employerDash.companyName")}</Label>
                <Input id="cp-name" value={profile.company_name || ""} onChange={setP("company_name")} className="mt-1.5" data-testid="cp-company-name" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="cp-industry">{t("employerDash.industry")}</Label>
                  <Input id="cp-industry" value={profile.industry || ""} onChange={setP("industry")} className="mt-1.5" data-testid="cp-industry" />
                </div>
                <div>
                  <Label htmlFor="cp-location">{t("employerDash.location")}</Label>
                  <Input id="cp-location" value={profile.location || ""} onChange={setP("location")} className="mt-1.5" data-testid="cp-location" />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="cp-website">{t("employerDash.website")}</Label>
                  <Input id="cp-website" placeholder="https://…" value={profile.website || ""} onChange={setP("website")} className="mt-1.5" data-testid="cp-website" />
                </div>
                <div>
                  <Label htmlFor="cp-size">{t("employerDash.companySize")}</Label>
                  <Input id="cp-size" placeholder="50-200" value={profile.company_size || ""} onChange={setP("company_size")} className="mt-1.5" data-testid="cp-size" />
                </div>
              </div>
              <div>
                <Label htmlFor="cp-about">{t("employerDash.about")}</Label>
                <Textarea id="cp-about" rows={3} value={profile.about || ""} onChange={setP("about")} className="mt-1.5" data-testid="cp-about" />
              </div>
            </div>
            <Button type="submit" disabled={saving} className="mt-6 w-full h-11 bg-indigo-900 hover:bg-indigo-800 text-base font-bold" data-testid="save-profile-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} {t("employerDash.saveProfile")}
            </Button>
            <p className="mt-3 text-xs text-slate-400">{t("employerDash.showcaseNote")}</p>
          </form>

          {/* Requirements list */}
          <div>
            <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">{t("employerDash.myRequirements")}</h2>
            {reqs.length === 0 ? (
              <div className="rounded-3xl border-2 border-dashed border-slate-200 bg-white p-12 text-center" data-testid="no-requirements">
                <Building2 className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-4 text-base text-slate-500">{t("employerDash.noRequirements")}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reqs.map((r) => (
                  <div key={r.req_id} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="requirement-row">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-heading text-lg font-bold text-slate-900">{r.designation}</h3>
                        <p className="text-sm text-slate-500">{r.company_name}</p>
                      </div>
                      <Badge className={`${STATUS_STYLES[r.status] || ""} capitalize`} data-testid="requirement-status">{t(`requirement.status.${r.status}`)}</Badge>
                    </div>
                    {r.skills?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {r.skills.map((s, i) => (
                          <span key={`${r.req_id}-${i}`} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">{s}</span>
                        ))}
                      </div>
                    )}
                    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-amber-600" /> {r.num_employees}</span>
                      {r.expected_date && <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-amber-600" /> {r.expected_date}</span>}
                      {r.offered_salary && <span className="flex items-center gap-1.5"><IndianRupee className="h-4 w-4 text-amber-600" /> {r.offered_salary}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Openings & applicants */}
        <div className="mt-12" data-testid="employer-openings">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">{t("employerDash.openings")}</h2>
          {jobs.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-slate-200 bg-white p-10 text-center" data-testid="no-openings">
              <Briefcase className="mx-auto h-9 w-9 text-slate-300" />
              <p className="mt-3 text-base text-slate-500">{t("employerDash.noOpenings")}</p>
            </div>
          ) : !activeJob ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {jobs.map((j) => (
                <button
                  key={j.job_id}
                  onClick={() => openJob(j)}
                  data-testid="opening-card"
                  className="text-left rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:border-amber-300 hover:shadow-md"
                >
                  <h3 className="font-heading text-lg font-bold text-slate-900">{j.title}</h3>
                  <p className="text-sm text-slate-500">{j.location}</p>
                  <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-amber-700">
                    <Users className="h-4 w-4" /> {j.application_count} {t("employerDash.applicants")}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div>
              <button onClick={() => setActiveJob(null)} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900" data-testid="back-to-openings">
                <ArrowLeft className="h-4 w-4" /> {t("employerDash.backToOpenings")}
              </button>
              <h3 className="font-heading text-lg font-bold text-slate-900">{activeJob.title} · {t("employerDash.applicants")}</h3>
              {loadingApps ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-amber-600" /></div>
              ) : applicants.length === 0 ? (
                <p className="py-8 text-center text-slate-400" data-testid="no-applicants">{t("employerDash.noApplicants")}</p>
              ) : (
                <div className="mt-4 space-y-3">
                  {applicants.map((a) => (
                    <div key={a.application_id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="applicant-row">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900">{a.applicant_name}</p>
                          <p className="text-sm text-slate-500">{a.applicant_email}</p>
                          {a.applicant_skills?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {a.applicant_skills.map((s, i) => (
                                <span key={`${a.application_id}-${i}`} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">{s}</span>
                              ))}
                            </div>
                          )}
                          {a.cover_note && <p className="mt-2 text-sm text-slate-500 italic">"{a.cover_note}"</p>}
                          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                            {a.resume_path && (
                              <a href={`${BACKEND}/api/applications/${a.application_id}/resume`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-amber-700 hover:underline" data-testid={`download-resume-${a.application_id}`}>
                                <FileText className="h-3.5 w-3.5" /> {t("employerDash.downloadResume")}
                              </a>
                            )}
                            {a.resume_link && <a href={a.resume_link} target="_blank" rel="noreferrer" className="inline-block text-sm font-semibold text-amber-700 hover:underline">{t("employerDash.viewResume")}</a>}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge className={a.status === "Shortlisted" ? "bg-emerald-100 text-emerald-700" : a.status === "Rejected" ? "bg-red-100 text-red-600" : "bg-slate-100 text-slate-600"} data-testid="applicant-status">
                            {t(`employerDash.appStatus.${a.status}`)}
                          </Badge>
                          {a.status !== "Shortlisted" && (
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setApplicantStatus(a.application_id, "Shortlisted")} data-testid={`shortlist-app-${a.application_id}`}>
                              <CheckCircle2 className="mr-1.5 h-4 w-4" /> {t("employerDash.shortlist")}
                            </Button>
                          )}
                          {a.status !== "Rejected" && (
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => setApplicantStatus(a.application_id, "Rejected")} data-testid={`reject-app-${a.application_id}`}>
                              <XCircle className="mr-1.5 h-4 w-4" /> {t("employerDash.reject")}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <WhatsAppButton />
    </div>
  );
}
